import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Image from 'components/global/Image';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN, AUTOCOMPLETE, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const defaultColSpan = {
	md: 6
};

const AddModuleModal = NiceModal.create(({ id, modalName }) => {
	const modal = useModal();
	const axios = useAxiosPrivate();
	const dispatch = useDispatch();
	const { showSuccess, showError } = useAlert();
	const { closeModal } = useModalRouter();
	const darkMode = useSelector((state) => state.layout.darkMode);
	const defaultImage = darkMode
		? 'https://storage.googleapis.com/tryoto-public/public/assets/images/emptySalesChannel-darkMode.png'
		: 'https://storage.googleapis.com/tryoto-public/public/assets/images/emptySalesChannel.png';

	const schema = useMemo(
		() =>
			yup.object().shape({
				moduleRoleID: yup.number().required(),
				moduleTitle: yup.string().required(),
				category: yup.string().required(),
				slug: yup.string().required(),
				relatedModules: yup.array().of(yup.string()).nullable(),
				isNew: yup.boolean().required(),
				isVisible: yup.boolean().required(),
				moduleIcon: yup.string().required(),
				settingsURL: yup.string().nullable()
			}),
		[]
	);

	// Form setup using react-hook-form

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const fillForm = useCallback(async () => {
		const defaultValues = {
			moduleRoleID: null,
			moduleTitle: null,
			category: null,
			moduleIcon: null,
			slug: null,
			relatedModules: [],
			isNew: false,
			isVisible: false,
			settingsURL: null
		};
		if (!id) {
			return defaultValues;
		}

		try {
			const response = await axios.post(ADMIN.CONFIGURATION.GET_MODULE, { id });

			const { data, status } = response;

			if (status !== 200) {
				showError();
				handleCloseModal();
				return;
			}
			return {
				...defaultValues,
				...data
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, [id]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const loading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);
	const onSubmit = (data) => {
		const url = id ? ADMIN.CONFIGURATION.UPDATE_MODULE : ADMIN.CONFIGURATION.ADD_MODULE;
		axios
			.post(url, {
				id,
				...data
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.MODULES));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				if (error.response?.data.code === 'error.OA001') {
					showError({
						message: 'error.thisCombinationAlreadyExists'
					});
				} else {
					AppLogger.error(error);
					showError();
				}
			});
	};
	const moduleAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'ID', width: 100 },
			{ accessor: 'roleName', width: 1000 }
		],
		[]
	);
	return (
		<Modal fullwidth name={modalName} maxWidth="medium" className="modal-addModule">
			<ModalTitle>
				<Typography variant="p1">addModule</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="moduleName" isLoading={loading} required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.SYSTEM_ROLES}
							value={watch('moduleRoleID')}
							setValue={(value) => {
								setValue('moduleRoleID', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							name="moduleRoleID"
							columns={moduleAutocompleteColumns}
							selectionLabelAccessor={['roleName']}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="moduleTitle" isLoading={loading} required>
						<StringInput name="moduleTitle" placeholder="enterModuleTitle" />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="slug" isLoading={loading} required>
						<StringInput name="slug" placeholder="enterSlug" />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="category" isLoading={loading} required>
						<EnumSelect
							enumName={ENUMS.MODULE_CATEGORIES}
							name="category"
							value={watch('category')}
							setValue={(value) => {
								setValue(`category`, value, { shouldValidate: true, shouldDirty: true });
							}}
							placeholder="selectCategory"
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="relatedModules" isLoading={loading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.SYSTEM_ROLES}
							value={watch('relatedModules')}
							setValue={(value) => {
								setValue('relatedModules', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							name="relatedModules"
							columns={moduleAutocompleteColumns}
							selectionLabelAccessor={['roleName']}
							multipleSelection
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="isNew" isLoading={loading} required>
						<SwitchInput name="isNew" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="isVisible" isLoading={loading} required>
						<SwitchInput name="isVisible" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="settingsURL" isLoading={loading}>
						<StringInput name="settingsURL" placeholder="enterSettingsURL" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="icon" isLoading={loading}>
						<StringInput name="moduleIcon" placeholder="icon" />
						<Image url={watch('moduleIcon')} width="8rem" height="8rem" defaultUrl={defaultImage} />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />

				<Button
					loading={loading}
					label={id ? 'save' : 'button.add'}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default AddModuleModal;
